import type * as React from "react";
import { toast as sonner } from "sonner";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";

/**
 * `useToast()` / `toast()` — the app-wide toast API, backed by sonner.
 *
 * There is exactly ONE toast renderer in the app: `<Toaster />` from
 * `components/ui/sonner.tsx`, mounted once in `App.tsx`. This module is the
 * call-site API on top of it, kept because ~60 call sites speak it; the Radix
 * toast store/renderer it used to own is gone.
 */
export type Toast = ToastProps & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

/** sonner takes the headline as its message; fall back to the description. */
function toast({ title, description, variant, duration, action }: Toast) {
  const message = title ?? description;
  const options = {
    description: title ? description : undefined,
    duration,
    action,
  };
  return variant === "destructive" ? sonner.error(message, options) : sonner.info(message, options);
}

function useToast() {
  return { toast, dismiss: sonner.dismiss };
}

export { useToast, toast };
